import { type CSSProperties, type ReactNode } from 'react';

export type IconName =
  | 'sparkle' | 'bolt' | 'plate' | 'heart' | 'ban'
  | 'user' | 'settings' | 'sun' | 'moon' | 'chev' | 'chevDown'
  | 'plus' | 'check' | 'x' | 'arrow' | 'refresh' | 'search'
  | 'bell' | 'cart' | 'clock' | 'flame' | 'leaf' | 'star'
  | 'grip' | 'swap' | 'egg' | 'trash' | 'menu' | 'home'
  | 'shield' | 'family' | 'cookpot' | 'barcode' | 'wallet'
  | 'list' | 'download' | 'share';

interface IconProps {
  name: IconName;
  size?: number;
  stroke?: number;
  className?: string;
  style?: CSSProperties;
  'aria-hidden'?: boolean;
}

const PATHS: Record<IconName, ReactNode> = {
  sparkle:  <><path d="M12 3l1.7 4.6L18 9.3l-4.3 1.7L12 15.6l-1.7-4.6L6 9.3l4.3-1.7L12 3z"/><path d="M19 15l.7 1.6L21 17l-1.3.4L19 19l-.7-1.6L17 17l1.3-.4L19 15z"/></>,
  bolt:     <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z"/>,
  plate:    <><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/></>,
  heart:    <path d="M12 21s-7-4.4-9.5-9C1 9 3 5 7 5c2 0 3.5 1.5 5 3 1.5-1.5 3-3 5-3 4 0 6 4 4.5 7-2.5 4.6-9.5 9-9.5 9z"/>,
  ban:      <><circle cx="12" cy="12" r="9"/><path d="M5.6 5.6l12.8 12.8"/></>,
  user:     <><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8"/></>,
  settings: <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/></>,
  sun:      <><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></>,
  moon:     <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z"/>,
  chev:     <path d="M9 6l6 6-6 6"/>,
  chevDown: <path d="M6 9l6 6 6-6"/>,
  plus:     <path d="M12 5v14M5 12h14"/>,
  check:    <path d="M5 12.5L10 17l9-10"/>,
  x:        <path d="M6 6l12 12M18 6L6 18"/>,
  arrow:    <><path d="M5 12h14"/><path d="M13 6l6 6-6 6"/></>,
  refresh:  <><path d="M3 12a9 9 0 0 1 15.5-6.3L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-15.5 6.3L3 16"/><path d="M3 21v-5h5"/></>,
  search:   <><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></>,
  bell:     <><path d="M6 8a6 6 0 1 1 12 0c0 7 3 8 3 8H3s3-1 3-8z"/><path d="M10 21a2 2 0 0 0 4 0"/></>,
  cart:     <><circle cx="9" cy="20" r="1.5"/><circle cx="18" cy="20" r="1.5"/><path d="M3 4h2l2.7 11h11l2-8H6"/></>,
  clock:    <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
  flame:    <path d="M12 22c4 0 7-3 7-7 0-3-2-5-3-6-1 1-1 3-3 3-1-2-1-5-3-7-1 4-5 5-5 10 0 4 3 7 7 7z"/>,
  leaf:     <><path d="M3 21c0-9 6-15 15-15-1 12-6 15-15 15z"/><path d="M9 15l9-9"/></>,
  star:     <path d="M12 2.8l2.8 6.2 6.7.5-5.1 4.4 1.6 6.6-6-3.6-6 3.6 1.6-6.6L2.5 9.5l6.7-.5L12 2.8z"/>,
  grip:     <><circle cx="9" cy="6" r="1.2"/><circle cx="15" cy="6" r="1.2"/><circle cx="9" cy="12" r="1.2"/><circle cx="15" cy="12" r="1.2"/><circle cx="9" cy="18" r="1.2"/><circle cx="15" cy="18" r="1.2"/></>,
  swap:     <><path d="M7 7h13"/><path d="M16 3l4 4-4 4"/><path d="M17 17H4"/><path d="M8 13l-4 4 4 4"/></>,
  egg:      <path d="M12 3c-4 0-7 6-7 11a7 7 0 0 0 14 0c0-5-3-11-7-11z"/>,
  trash:    <><path d="M4 7h16"/><path d="M9 7V4h6v3"/><path d="M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13"/></>,
  menu:     <><path d="M3 6h18"/><path d="M3 12h18"/><path d="M3 18h18"/></>,
  home:     <><path d="M3 11l9-8 9 8"/><path d="M5 10v10h14V10"/></>,
  shield:   <path d="M12 3l8 3v6c0 5-4 8-8 9-4-1-8-4-8-9V6l8-3z"/>,
  family:   <><circle cx="9" cy="8" r="3"/><circle cx="17" cy="9" r="2"/><path d="M3 20c0-3 3-5 6-5s6 2 6 5"/><path d="M15 20c0-2 2-4 4-4"/></>,
  cookpot:  <><path d="M3 9h18"/><path d="M5 9v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9"/><path d="M8 5l1 4M16 5l-1 4M12 4v5"/></>,
  barcode:  <><path d="M4 6v12M7 6v12M10 6v12M13 6v12M16 6v12M19 6v12"/></>,
  wallet:   <><rect x="3" y="6" width="18" height="13" rx="2"/><path d="M16 13a1 1 0 1 0 2 0 1 1 0 0 0-2 0z"/><path d="M3 10h18"/></>,
  list:     <><path d="M8 6h13M8 12h13M8 18h13"/><circle cx="4" cy="6" r="1"/><circle cx="4" cy="12" r="1"/><circle cx="4" cy="18" r="1"/></>,
  download: <><path d="M12 3v12"/><path d="M7 10l5 5 5-5"/><path d="M5 21h14"/></>,
  share:    <><circle cx="6" cy="12" r="2.5"/><circle cx="18" cy="6" r="2.5"/><circle cx="18" cy="18" r="2.5"/><path d="M8.2 10.8l7.6-3.6M8.2 13.2l7.6 3.6"/></>,
};

export function Icon({ name, size = 18, stroke = 1.7, className, style, 'aria-hidden': ariaHidden = true }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      aria-hidden={ariaHidden}
    >
      {PATHS[name]}
    </svg>
  );
}
